import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { RegisteredStudent } from '../api/families';

export type StudentStatus = 'not_checked_in' | 'waiting' | 'dismissed';

export interface StudentStatusInfo {
  status: StudentStatus;
  checkedInAt?: string;
  dismissedAt?: string;
}

interface StudentStatusCardProps {
  student: RegisteredStudent;
  statusInfo: StudentStatusInfo;
  onEdit: (student: RegisteredStudent) => void;
  onDelete: (student: RegisteredStudent) => void;
}

const GRADE_LABELS: Record<string, string> = {
  'K': 'Kindergarten',
  '1': '1st Grade',
  '2': '2nd Grade',
  '3': '3rd Grade',
  '4': '4th Grade',
  '5': '5th Grade',
  '6': '6th Grade',
  '7': '7th Grade',
  '8': '8th Grade',
};

const getStatusConfig = (status: StudentStatus) => {
  switch (status) {
    case 'waiting':
      return {
        icon: <HourglassEmptyIcon />,
        label: 'Waiting for Pickup',
        color: 'warning' as const,
        cardVariant: 'student-waiting' as const,
      };
    case 'dismissed':
      return {
        icon: <CheckCircleIcon />,
        label: 'Dismissed',
        color: 'success' as const,
        cardVariant: 'student-ready' as const,
      };
    case 'not_checked_in':
    default:
      return {
        icon: <RemoveCircleOutlineIcon />,
        label: 'Not Checked In',
        color: 'default' as const,
        cardVariant: 'student-not-checked-in' as const,
      };
  }
};

const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const getWaitTime = (checkedInAt: string): string => {
  const now = Date.now();
  const checkedIn = new Date(checkedInAt).getTime();
  const diffMinutes = Math.floor((now - checkedIn) / 60000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes === 1) return '1 min';
  return `${diffMinutes} mins`;
};

const StudentStatusCard: React.FC<StudentStatusCardProps> = ({
  student,
  statusInfo,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const statusConfig = getStatusConfig(statusInfo.status);

  return (
    <Card
      variant={statusConfig.cardVariant}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
        {/* Header Row - Status Chip */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Chip
            icon={statusConfig.icon}
            label={statusConfig.label}
            color={statusConfig.color}
            size={isMobile ? 'small' : 'medium'}
            sx={{ fontWeight: 600 }}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => onEdit(student)}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete(student)}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'error.main' },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Student Name */}
        <Typography
          variant="cardTitle"
          sx={{ mb: 1 }}
        >
          {student.first_name} {student.last_name}
        </Typography>

        {/* Grade and Pickup Type */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Chip
            label={GRADE_LABELS[student.grade] || student.grade}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={student.pickup_type === 'car' ? <DirectionsCarIcon /> : <DirectionsWalkIcon />}
            label={student.pickup_type === 'car' ? 'Car Pickup' : 'Walker'}
            size="small"
            variant="outlined"
          />
        </Box>

        {/* Wait Time / Dismissal Time */}
        {statusInfo.status === 'waiting' && statusInfo.checkedInAt && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
              Checked in at {formatTime(statusInfo.checkedInAt)}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'warning.dark' }}>
              Waiting: {getWaitTime(statusInfo.checkedInAt)}
            </Typography>
          </Box>
        )}

        {statusInfo.status === 'dismissed' && statusInfo.dismissedAt && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: (theme) => theme.palette.success.light + '30', borderRadius: 1 }}>
            <Typography variant="statusText" color="success.dark">
              Ready for pickup!
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Dismissed at {formatTime(statusInfo.dismissedAt)}
            </Typography>
          </Box>
        )}

        {/* Medical Notes */}
        {student.medical_notes && (
          <Alert
            severity="warning"
            icon={<MedicalServicesIcon />}
            sx={{
              mt: 2,
              py: 0.5,
              px: 1.5,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
            }}
          >
            <strong>Medical:</strong> {student.medical_notes}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default StudentStatusCard;
